/**
 * アクセス可能な現場の選択肢を取得するフック
 *
 * DEV/MCM(管理者): `GET /api/sites` で全現場を取得。
 * SITE_*(非管理者): API 権限なしのため呼ばず、空配列を返す
 * (SITE_USER の自サイトは呼出側で `user.site` から構築する)。
 */
import { useEffect, useState } from 'react';
import { logger } from '@/shared/lib/logger';
import { isAdmin } from '@/features/auth/utils/permissionUtils';
import { getAllSites } from '../services/siteApiService';
import type { User } from '@/features/auth/types/auth.types';
import type { SiteOption } from '@/features/purchase-orders/types/purchaseOrder';

interface UseAccessibleSiteOptionsResult {
  readonly accessibleSites: readonly SiteOption[];
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * 管理者(MCM/DEV)用に全現場の `{ value, label }` 選択肢を返す。
 * 非管理者の場合は空配列(API 呼出なし、SITE_USER の自サイトは呼出側で構築)。
 */
export function useAccessibleSiteOptions(params: {
  readonly user: User | null;
}): UseAccessibleSiteOptionsResult {
  const [accessibleSites, setAccessibleSites] = useState<readonly SiteOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isAdmin(params.user)) {
      // SITE_*(非管理者)は API 呼出不要、空配列で確定
      setAccessibleSites([]);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const fetchSites = async (): Promise<void> => {
      try {
        const sites = await getAllSites();
        if (!isMounted) {
          return;
        }
        const options: readonly SiteOption[] = sites.map((site) => ({
          value: site.id,
          label: site.siteName,
        }));
        setAccessibleSites(options);
      } catch (caughtError) {
        if (!isMounted) {
          return;
        }
        const errorInstance =
          caughtError instanceof Error
            ? caughtError
            : new Error('現場一覧の取得に失敗しました');
        setError(errorInstance);
        logger.captureError(caughtError, 'useAccessibleSiteOptions.fetchSites');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchSites();

    return () => {
      isMounted = false;
    };
  }, [params.user]);

  return { accessibleSites, isLoading, error };
}
